import { Box, Button, TextField } from "@mui/material";
import { useState } from "react";

interface CommentInputProps {
  onSubmit: (comment: string) => Promise<void>;
  loading?: boolean;
  placeholder?: string;
  buttonText?: string;
  rows?: number;
  disabled?: boolean;
}

export function CommentInput({
  onSubmit,
  loading = false,
  placeholder = "Add a comment...",
  buttonText = "Add Comment",
  rows = 3,
  disabled = false,
}: CommentInputProps) {
  const [comment, setComment] = useState("");

  const handleSubmit = async () => {
    if (!comment.trim() || loading) return;

    await onSubmit(comment.trim());
    setComment("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // NOTE: Enter submits comment, Shift+Enter adds carriage return (line break)
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (comment.trim()) {
        // If there's content, clear it and keep focus
        setComment("");
        (event.target as HTMLTextAreaElement).focus();
      } else {
        // If no content, let the event bubble up to close the dialog
        // Remove focus from the textarea to allow dialog to handle the escape
        (event.target as HTMLTextAreaElement).blur();
      }
    }
  };

  return (
    <Box>
      <TextField
        multiline
        rows={rows}
        fullWidth
        variant="outlined"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={placeholder}
        disabled={loading || disabled}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <Box sx={{ right: 8 }}>
              <Button
                onClick={handleSubmit}
                disabled={!comment.trim() || loading || disabled}
                variant="contained"
                size="small"
              >
                {loading ? "Adding..." : buttonText}
              </Button>
            </Box>
          ),
        }}
      />
    </Box>
  );
}
